/**
 * AzureFinOpsTab — Aba de FinOps Azure dentro de GovernanceCostsPage
 * 
 * Seções:
 * 1. Dashboard (KPIs)
 * 2. Inventário
 * 3. Recomendações
 * 4. Rightsizing
 * 5. Coleta Manual
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Server, AlertTriangle, TrendingDown, Check, X, RefreshCw, 
  Database, Search, Cpu, HardDrive, Cloud, ChevronDown, ChevronRight,
  Shield, Zap
} from 'lucide-react';
import {
  getFinOpsDashboard,
  getFinOpsInventory,
  getFinOpsRecommendations,
  approveFinOpsRecommendation,
  getFinOpsRightsizing,
  triggerFinOpsCollect,
  getFinOpsResourceMetrics,
} from '../../services/finopsService';
import { COLORS } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

// === BADGES ===
const RiskBadge = ({ level }) => {
  const styles = {
    low: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    high: 'bg-orange-100 text-orange-700',
    critical: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${styles[level] || 'bg-gray-100 text-gray-700'}`}>
      {level}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    executed: 'bg-green-100 text-green-700',
    dismissed: 'bg-gray-100 text-gray-500',
    unreviewed: 'bg-gray-100 text-gray-600',
    candidate: 'bg-amber-100 text-amber-700',
    optimized: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// === KPI CARD ===
const KpiCard = ({ label, value, subtext, icon: Icon, color = COLORS.primary }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
        {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
      </div>
    </div>
  </div>
);

// === RESOURCE TYPE ICON ===
const ResourceIcon = ({ type }) => {
  if (type?.includes('Sql')) return <Database size={16} className="text-blue-600" />;
  if (type?.includes('Search')) return <Search size={16} className="text-purple-600" />;
  if (type?.includes('Compute') || type?.includes('virtualMachines')) return <Cpu size={16} className="text-orange-600" />;
  if (type?.includes('Storage')) return <HardDrive size={16} className="text-green-600" />;
  if (type?.includes('Web')) return <Cloud size={16} className="text-sky-600" />;
  return <Server size={16} className="text-gray-500" />;
};

// === MAIN COMPONENT ===
const AzureFinOpsTab = () => {
  const [dashboard, setDashboard] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [rightsizing, setRightsizing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectResult, setCollectResult] = useState(null);
  const [expandedResource, setExpandedResource] = useState(null);
  const [resourceMetrics, setResourceMetrics] = useState({});
  const [section, setSection] = useState('dashboard');
  const { lang } = useGovernanceLang();
  const T = {
    pt: {
      loading: 'Carregando FinOps Azure...', error: 'Erro ao carregar FinOps',
      overview: 'Visão Geral', rightsizing: 'Rightsizing',
      inventory: 'Inventário', recommendations: 'Recomendações',
      monthlyCost: 'Custo Mensal Estimado', potentialSavings: 'Economia Potencial',
      monitored: 'Recursos Monitorados', aiBudget: 'AI Budget (mês)',
      recsLabel: (n) => `${n} recomendações`, candidatesLabel: (n) => `${n} candidatos`,
      collectTitle: 'Coleta de Métricas Azure',
      collectDesc: 'Atualiza DTU, CPU e métricas via Azure Monitor (requer az CLI no backend)',
      collecting: 'Coletando...', collectBtn: 'Atualizar Métricas',
      collectOk: (n) => `✅ Coleta concluída: ${n} métricas coletadas`,
      collectErr: (e) => `❌ Erro: ${e}`,
      inventoryTitle: (n) => `Inventário Azure (${n} recursos)`,
      totalLabel: 'Total', perMonth: '/mês',
      noMetrics: 'Sem métricas disponíveis para este recurso',
      noRecs: 'Nenhuma recomendação pendente',
      current: 'Atual', projected: 'Projetado', savings: 'Economia',
      seeEvidence: 'Ver evidências',
      approve: 'Aprovar', approveErr: 'Erro ao aprovar',
      rightsizingTitle: 'Candidatos a Rightsizing',
      rightsizingDesc: 'Recursos over-provisioned baseado em métricas reais',
      noRightsizing: 'Nenhum candidato identificado (execute coleta para atualizar)',
      currentSku: 'SKU atual', utilization: 'Utilização',
    },
    en: {
      loading: 'Loading Azure FinOps...', error: 'Error loading FinOps',
      overview: 'Overview', rightsizing: 'Rightsizing',
      inventory: 'Inventory', recommendations: 'Recommendations',
      monthlyCost: 'Estimated Monthly Cost', potentialSavings: 'Potential Savings',
      monitored: 'Monitored Resources', aiBudget: 'AI Budget (mo)',
      recsLabel: (n) => `${n} recommendations`, candidatesLabel: (n) => `${n} candidates`,
      collectTitle: 'Azure Metrics Collection',
      collectDesc: 'Updates DTU, CPU and metrics via Azure Monitor (requires az CLI on backend)',
      collecting: 'Collecting...', collectBtn: 'Update Metrics',
      collectOk: (n) => `✅ Collection complete: ${n} metrics collected`,
      collectErr: (e) => `❌ Error: ${e}`,
      inventoryTitle: (n) => `Azure Inventory (${n} resources)`,
      totalLabel: 'Total', perMonth: '/mo',
      noMetrics: 'No metrics available for this resource',
      noRecs: 'No pending recommendations',
      current: 'Current', projected: 'Projected', savings: 'Savings',
      seeEvidence: 'View evidence',
      approve: 'Approve', approveErr: 'Error approving',
      rightsizingTitle: 'Rightsizing Candidates',
      rightsizingDesc: 'Over-provisioned resources based on real metrics',
      noRightsizing: 'No candidates identified (run collection to update)',
      currentSku: 'Current SKU', utilization: 'Utilization',
    },
  }[lang] || {};

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, invRes, recRes, rsRes] = await Promise.all([
        getFinOpsDashboard().catch(() => null),
        getFinOpsInventory().catch(() => ({ data: [] })),
        getFinOpsRecommendations().catch(() => ({ data: [] })),
        getFinOpsRightsizing().catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes?.data || null);
      setInventory(invRes?.data || []);
      setRecommendations(recRes?.data || []);
      setRightsizing(rsRes?.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCollect = async () => {
    setCollecting(true);
    setCollectResult(null);
    try {
      const result = await triggerFinOpsCollect();
      setCollectResult({ success: true, ...result });
      // Refresh data after collection
      setTimeout(() => fetchAll(), 1000);
    } catch (err) {
      setCollectResult({ success: false, error: err.message });
    } finally {
      setCollecting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveFinOpsRecommendation(id);
      setRecommendations(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
    } catch (err) {
      alert(`${T.approveErr}: ${err.message}`);
    }
  };

  const handleExpandResource = async (resourceName) => {
    if (expandedResource === resourceName) {
      setExpandedResource(null);
      return;
    }
    setExpandedResource(resourceName);
    if (!resourceMetrics[resourceName]) {
      try {
        const res = await getFinOpsResourceMetrics(resourceName);
        setResourceMetrics(prev => ({ ...prev, [resourceName]: res.data || [] }));
      } catch (err) {
        setResourceMetrics(prev => ({ ...prev, [resourceName]: [] }));
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <RefreshCw size={32} className="animate-spin text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">{T.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <AlertTriangle size={20} className="inline mr-2 text-red-600" />
        <span className="text-red-700">{T.error}: {error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'dashboard', label: T.overview, icon: Zap },
          { key: 'inventory', label: `${T.inventory} (${inventory.length})`, icon: Server },
          { key: 'recommendations', label: `${T.recommendations} (${recommendations.length})`, icon: TrendingDown },
          { key: 'rightsizing', label: T.rightsizing, icon: Cpu },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setSection(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              section === tab.key
                ? 'text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            style={section === tab.key ? { backgroundColor: COLORS.primary } : {}}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* === DASHBOARD === */}
      {section === 'dashboard' && dashboard && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label={T.monthlyCost}
              value={`$${(dashboard.total_monthly_estimate_usd || 0).toFixed(0)}`}
              icon={Cloud}
              color="#064e3b"
            />
            <KpiCard
              label={T.potentialSavings}
              value={`$${(dashboard.potential_savings_usd || 0).toFixed(0)}`}
              subtext={T.recsLabel(dashboard.pending_recommendations || 0)}
              icon={TrendingDown}
              color="#b48c2c"
            />
            <KpiCard
              label={T.monitored}
              value={dashboard.total_resources || 0}
              subtext={T.candidatesLabel(dashboard.optimization_candidates || 0)}
              icon={Server}
              color="#0369a1"
            />
            <KpiCard
              label={T.aiBudget}
              value={`$${(dashboard.ai_cost_this_month_usd || 0).toFixed(2)}`}
              icon={Cpu}
              color="#7c3aed"
            />
          </div>

          {/* Collect Button */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900">{T.collectTitle}</h3>
              <p className="text-sm text-gray-500">{T.collectDesc}</p>
            </div>
            <button
              onClick={handleCollect}
              disabled={collecting}
              className="px-4 py-2 rounded-lg text-white font-semibold flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: COLORS.primary }}
            >
              <RefreshCw size={16} className={collecting ? 'animate-spin' : ''} />
              {collecting ? T.collecting : T.collectBtn}
            </button>
          </div>
          {collectResult && (
            <div className={`rounded-lg p-3 text-sm ${collectResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {collectResult.success
                ? T.collectOk(collectResult.metrics_collected || 0)
                : T.collectErr(collectResult.error)}
            </div>
          )}
        </>
      )}

      {/* === INVENTORY === */}
      {section === 'inventory' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">{T.inventoryTitle(inventory.length)}</h3>
            <span className="text-sm text-gray-500">
              {T.totalLabel}: ${inventory.reduce((s, r) => s + (r.monthly_cost_estimate_usd || 0), 0).toFixed(0)}{T.perMonth}
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {inventory.map((res) => (
              <div key={res.id || res.resource_id}>
                <div
                  className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleExpandResource(res.resource_name)}
                >
                  <ResourceIcon type={res.resource_type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm truncate">{res.resource_name}</span>
                      <StatusBadge status={res.optimization_status} />
                    </div>
                    <div className="text-xs text-gray-500">
                      {res.resource_group} • {res.location} • {res.sku_name || res.sku_tier || '—'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 text-sm">
                      {res.monthly_cost_estimate_usd > 0 ? `$${res.monthly_cost_estimate_usd.toFixed(0)}${T.perMonth}` : '—'}
                    </div>
                    <div className="text-xs text-gray-400">{res.status}</div>
                  </div>
                  {expandedResource === res.resource_name ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
                {/* Expanded metrics */}
                {expandedResource === res.resource_name && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                    {(resourceMetrics[res.resource_name] || []).length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {resourceMetrics[res.resource_name].map((m, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 border">
                            <div className="text-xs text-gray-500">{m.metric_name} ({m.aggregation})</div>
                            <div className="text-lg font-bold text-gray-900">
                              {typeof m.metric_value === 'number' ? m.metric_value.toFixed(2) : m.metric_value}
                            </div>
                            <div className="text-xs text-gray-400">
                              {m.period_start?.split('T')[0]} → {m.period_end?.split('T')[0]}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">{T.noMetrics}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === RECOMMENDATIONS === */}
      {section === 'recommendations' && (
        <div className="space-y-4">
          {recommendations.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Check size={32} className="text-green-400 mx-auto mb-2" />
              <p className="text-gray-500">{T.noRecs}</p>
            </div>
          ) : (
            recommendations.map((rec) => (
              <div key={rec.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <RiskBadge level={rec.priority} />
                      <StatusBadge status={rec.status} />
                      <span className="text-xs text-gray-400 uppercase">{rec.category}</span>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">{rec.title}</h4>
                    <p className="text-sm text-gray-600 mb-3">{rec.description}</p>
                    <div className="flex gap-4 text-sm">
                      <span className="text-gray-500">
                        {T.current}: <span className="font-bold text-gray-900">${rec.current_cost_usd?.toFixed(0)}/{T.perMonth.replace('/','')}</span>
                      </span>
                      <span className="text-gray-500">
                        {T.projected}: <span className="font-bold text-green-700">${rec.projected_cost_usd?.toFixed(0)}/{T.perMonth.replace('/','')}</span>
                      </span>
                      <span className="text-gray-500">
                        {T.savings}: <span className="font-bold text-emerald-600">${rec.savings_usd?.toFixed(0)}/{T.perMonth.replace('/','')}</span>
                      </span>
                    </div>
                    {rec.evidence_json && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                          {T.seeEvidence}
                        </summary>
                        <pre className="mt-1 text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                          {typeof rec.evidence_json === 'string' 
                            ? JSON.stringify(JSON.parse(rec.evidence_json), null, 2)
                            : JSON.stringify(rec.evidence_json, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {rec.status === 'pending' && (
                      <button
                        onClick={() => handleApprove(rec.id)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 flex items-center gap-1"
                      >
                        <Check size={14} /> {T.approve}
                      </button>
                    )}
                    <RiskBadge level={rec.risk_level} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* === RIGHTSIZING === */}
      {section === 'rightsizing' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-bold text-gray-900">{T.rightsizingTitle}</h3>
            <p className="text-xs text-gray-500">{T.rightsizingDesc}</p>
          </div>
          {rightsizing.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Shield size={32} className="text-green-400 mx-auto mb-2" />
              <p>{T.noRightsizing}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {rightsizing.map((item, idx) => (
                <div key={idx} className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.resource_name}</h4>
                      <p className="text-sm text-gray-600">{item.recommendation}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                      <span>{T.currentSku}: <b>{item.current_sku}</b></span>
                        <span>{T.utilization}: <b>{item.utilization_pct?.toFixed(2)}%</b></span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-emerald-600 text-lg">
                        -{item.potential_savings?.toFixed(0)}{T.perMonth}
                      </div>
                      <RiskBadge level={item.risk} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AzureFinOpsTab;
